import React, {useEffect, useState} from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    ReferenceLine,
    ResponsiveContainer,
    Cell,
    YAxis
} from 'recharts';
import {EVENTS_TAB_INDEX, FETCH_INTERVAL, iOS, ODDS_TAB_INDEX} from "../../utils";
import {useAppDispatch, useAppSelector} from "../../redux";
import {details, getLiveOdds, getMomentum} from "../../redux/slices/fixtures";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import {updateGameActiveTab} from "../../redux/slices/settings";

import './index.scss'

interface IProps {
    gameId: string;
}

const SoccerGameTimeline: React.FC<IProps> = ({gameId}) => {
    const dispatch = useAppDispatch()

    const {momentum: gameData, activeGame} = useAppSelector(state => state.fixtures)

    useEffect(() => {
        let timer: any = null;

        const fetchDataOnInterval = async () => {
            try {
                dispatch(getMomentum(gameId))

                if (activeGame.status.type !== 'inprogress' && timer) {
                    clearInterval(timer);
                }
            } catch (error) {
                clearInterval(timer);
            }
        };

        fetchDataOnInterval();
        timer = setInterval(fetchDataOnInterval, FETCH_INTERVAL);

        return () => {
            if (timer) {
                clearInterval(timer);
            }
        }
    }, [activeGame.id]);

    const goToEventsTab = () => {
        dispatch(updateGameActiveTab(EVENTS_TAB_INDEX))
    }

    const CustomTick = (props) => {
        const { x, y, payload } = props;
        let displayValue = '';
        let color = "#e0e0e0"
        if (['15', '30', '45', '60', '75', '90', '105', '120'].includes(payload.value)) {
            displayValue = payload.value
        }

        if (['45', '90', '105', '120'].includes(payload.value)) {
            color = "var(--game-details-team-name-color)"
        }

        const majorTicks = ['15', '30', '45', '60', '75', '90', '105', '120'];
        const isMajorTick = majorTicks.includes(payload.value);
        const moment = gameData.find((moment: any) => moment.minute === payload.value) as any;

        return (
            <>
                {isMajorTick && (
                    <line
                        x1={x}
                        y1={y}
                        x2={x}
                        y2={20}
                        stroke="#e0e0e0"
                        strokeWidth={1}
                        style={{zIndex: '-1'}}
                    />
                )}

                {moment?.goal && (
                    <foreignObject x={x} y={moment.goal.isHome === '1' ? y - 108 : y - 8} width="14" height="14"
                                   style={{display: 'flex', cursor: 'pointer'}} onClick={goToEventsTab}>
                        {
                            moment.goal.incidentClass === 'penalty' ? (
                                <svg width="14" height="14" viewBox="0 0 24 24"
                                     className="SvgWrapper fyGiev"
                                     fill="var(--momentum-color)"
                                >
                                    <path
                                        d="M12 8.5c3.72 0 6.75 3.03 6.75 6.75S15.72 22 12 22s-6.75-3.03-6.75-6.75S8.28 8.5 12 8.5zm-.01 1.67c-1.29 0-2.47.5-3.37 1.3l.88.53-.64 2.39-1.94.69v.17c0 .65.13 1.26.35 1.83l.9-.46 1.5 1.67-.24 1.33a5.017 5.017 0 0 0 2.29.702l.276.008.198-.004a5.089 5.089 0 0 0 4.862-4.862l.004-.214c0-1.46-.62-2.77-1.61-3.7l-.84.78-2.62-1.11.28-1.04c-.1-.01-.19-.01-.28-.01zm2.42 3.95 1.34 2.46-2 2.2-2-.78v-2.91l2.66-.97zM22 2v9h-2V4H4v7H2V2h20z"
                                    ></path>
                                </svg>) :
                                <SportsSoccerIcon style={{width: '14px', height: '14px'}}/>
                        }
                    </foreignObject>
                )}
                {
                    moment?.yellowCards?.map((yc) => (
                        <foreignObject x={x} y={yc.isHome === '1' ? y - 108 : y - 8} width="14"
                                       height="14"
                                       style={{display: 'flex', cursor: 'pointer'}} onClick={goToEventsTab}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                 focusable="false"
                                 className="kz-icon-xxs icon--color-afrodite scoreboard-summary__item__icon yellow-card">
                                <rect width="14" height="18" x="5" y="3" rx="2"></rect>
                            </svg>
                        </foreignObject>
                    ))
                }
                {
                    moment?.yellowReds?.map((yr) => (
                        <foreignObject x={x} y={yr.isHome === '1' ? y - 108 : y - 8} width="14"
                                       height="14"
                                       style={{display: 'flex', cursor: 'pointer'}} onClick={goToEventsTab}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                 focusable="false"
                                 className="kz-icon-xxs icon--color-afrodite scoreboard-summary__item__icon yellow-card">
                                <rect width="14" height="18" x="5" y="3" rx="2"></rect>
                            </svg>
                        </foreignObject>
                    ))
                }
                {
                    moment?.redCards?.map((rc) => (
                        <foreignObject x={x} y={rc.isHome === '1' ? y - 108 : y - 8} width="14"
                                       height="14"
                                       style={{display: 'flex', cursor: 'pointer'}} onClick={goToEventsTab}>
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"
                                 focusable="false"
                                 className="kz-icon-xxs icon--color-afrodite scoreboard-summary__item__icon yellow-card">
                                <rect width="14" height="18" x="5" y="3" rx="2"></rect>
                            </svg>
                        </foreignObject>
                    ))
                }
                {
                    moment?.substitutions?.map((substitution: any) => (
                        <foreignObject x={x} y={substitution.isHome === '1' ? y - 108 : y - 5} width="14"
                                       height="14"
                                       style={{display: 'flex', cursor: 'pointer'}} onClick={goToEventsTab}>
                            <svg className="sr-momentum__event-icon" xmlns="http://www.w3.org/2000/svg" version="1.2"
                                 viewBox="0 0 8 7" width="14">
                                <path className="srt-fill-soccer-substitution-in"
                                      d="M4 2.16L2 0 0 2.16l.018.05H1.02V7h1.958V2.21H3.98" fill="#4fbe30"></path>
                                <path d="M8.02 4.837l-2 2.16-2-2.16.017-.05H5.04v-4.79h1.957v4.79H8"
                                      className="srt-fill-soccer-substitution-out" fill="#e43b3b"></path>
                            </svg>
                        </foreignObject>
                    ))
                }
                {/*{moment?.corner && (*/}
                {/*    <foreignObject x={x} y={moment.corner.isHome ? y - 100 : y - 5} width="14"*/}
                {/*                   height="14"*/}
                {/*                   style={{display: 'flex'}}>*/}
                {/*        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" role="presentation"*/}
                {/*             focusable="false" className="kz-icon-xxs icon--color-white-snow"*/}
                {/*             fill="var(--momentum-color)"*/}
                {/*        >*/}
                {/*            <path*/}
                {/*                d="M18 7.136L10.615 4v14.4H8.77v-1.384C7.117 17.296 6 17.808 6 18.4c0 .88 2.483 1.6 5.538 1.6 3.056 0 5.539-.72 5.539-1.6 0-.792-1.994-1.448-4.615-1.576v-7.24L18 7.136z"></path>*/}
                {/*        </svg>*/}
                {/*    </foreignObject>*/}
                {/*)}*/}
                {moment?.shotsOffTarget?.map((sft: any) => (
                    <foreignObject x={x} y={sft.isHome ? y - 108 : y - 8} width="14"
                                   height="14"
                                   style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <svg className="sr-momentum__event-icon srm-circle_empty srt-stroke-home-1" viewBox="0 0 16 16">
                            <circle cx="6" cy="6" r="4"></circle>
                        </svg>
                    </foreignObject>
                ))}
                {moment?.shotsOnTarget?.map((sot: any) => (
                    <foreignObject x={x} y={sot.isHome ? y - 108 : y - 8} width="14"
                                   height="14"
                                   style={{display: 'flex'}}>
                        <svg className="sr-momentum__event-icon srm-circle_full srt-stroke-home-1 srt-fill-home-1"
                             viewBox="0 0 16 16">
                            <circle cx="6" cy="6" r="4"></circle>
                        </svg>
                    </foreignObject>
                ))}

                <text x={x} y={y + 16} fill={color} textAnchor="middle" fontSize={10}>
                    {displayValue && displayValue + "'"}
                </text>
            </>
        );
    };

    // const gradientOffset = () => {
    //     const dataMax = Math.max(...gameData.map((i: any) => i.value));
    //     const dataMin = Math.min(...gameData.map((i: any) => i.value));
    //
    //     if (dataMax <= 0) {
    //         return 0;
    //     }
    //     if (dataMin >= 0) {
    //         return 1;
    //     }
    //
    //     return dataMax / (dataMax - dataMin);
    // };
    //
    // const off = gradientOffset();

    return (
        <div style={{position: 'relative'}}>
            <ResponsiveContainer width="100%" height={150} style={{marginBottom: "-10px"}}>
                <BarChart
                    data={gameData}
                    stackOffset="sign"
                    margin={{top: 20, bottom: 20}}
                >
                    <ReferenceLine y={0} stroke="#e0e0e0"/>
                    <XAxis
                        dataKey="minute"
                        interval={0}
                        tick={<CustomTick />}
                        tickLine={false}
                        axisLine={false}
                    >
                    </XAxis>

                    <YAxis
                        domain={[-100, 100]}
                        hide={true}
                    />

                    <Bar dataKey="value" animationBegin={0} animationDuration={0}>
                        {gameData?.map(({value, minute}) => (
                            <Cell key={`cell-${minute}`}
                                  fill={parseInt(value, 10) >= 0 ? 'var(--momentum-color)' : 'var(--primary-color)'}/>
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/*<ResponsiveContainer width="100%" height={150}>*/}
            {/*    <AreaChart*/}
            {/*        data={gameData.map((i: any) => ({name: i.minute, uv: i.value}))}*/}
            {/*        stackOffset="sign"*/}
            {/*        margin={{top: 20, bottom: 20}}*/}
            {/*    >*/}
            {/*        <ReferenceLine y={0} stroke="#e0e0e0"/>*/}
            {/*        <XAxis*/}
            {/*            dataKey="name"*/}
            {/*            interval={0}*/}
            {/*            tick={<CustomTick />}*/}
            {/*            tickLine={false}*/}
            {/*            axisLine={false}*/}
            {/*        >*/}
            {/*        </XAxis>*/}
            {/*        <YAxis*/}
            {/*            domain={[-100, 100]}*/}
            {/*            hide={true}*/}
            {/*        />*/}
            {/*        <defs>*/}
            {/*            <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">*/}
            {/*                <stop offset={off} stopColor="var(--momentum-color)"  />*/}
            {/*                <stop offset={off} stopColor="var(--primary-color)"  />*/}
            {/*            </linearGradient>*/}
            {/*        </defs>*/}
            {/*        <Area type="monotone" dataKey="uv" stroke="transparent" fill="url(#splitColor)" />*/}
            {/*    </AreaChart>*/}
            {/*</ResponsiveContainer>*/}
            {
                activeGame.pressure &&
                <div className='pressure-section'>
                    <svg viewBox="0 0 36 32"
                         className="sr-pulse-indicator__main-icon srt-text-secondary srm-prev srm-v1">
                        <path
                            d="M23.1 0c1.8 0 3.3 1.5 3.3 3.4 0 1.8-1.5 3.3-3.3 3.4H23c-1.8 0-3.3-1.5-3.3-3.4A3.5 3.5 0 0 1 23.1 0zm0 1.7c-.9 0-1.7.8-1.7 1.7v.2c.1.8.8 1.4 1.6 1.4h.1c.9 0 1.6-.8 1.6-1.6 0-1-.7-1.7-1.6-1.7zM26.5 8c2.2 0 4 1.7 4.2 3.9l.3 8.3c0 1.5-1.2 2.6-2.6 2.6-.3 0-.6-.1-.9-.2l-.2-.1v6.7c0 1.4-1 2.5-2.4 2.6h-.2c-.6 0-1.2-.2-1.6-.6l-.1.1c-.3.3-.7.5-1.2.6h-.5c-1.3 0-2.4-1-2.6-2.4v-7l-.2.1c-.2.1-.4.1-.6.1h-.2c-1.3 0-2.4-1-2.6-2.4v-.4l.3-7.9c-.1-2 1.6-3.8 3.8-4h7.3zm7 19c1.4 0 2.5 1.1 2.5 2.5S34.9 32 33.5 32 31 30.9 31 29.5s1.1-2.5 2.5-2.5zm-7-17.4h-7.2a2.7 2.7 0 0 0-2.4 2.6l-.3 8v.2c.1.5.5.9 1 .9.6 0 1-.5 1-1v-5.1c.1-.4.4-.7.8-.7h.1c.4.1.6.4.6.8V29.6c.1.5.5.9 1 .9h.2c.5-.1.9-.5.9-1v-8.1h1.5V29.7c.1.5.5.9 1 .9.6 0 1-.5 1-1V15.5c0-.5.4-.8.8-.8s.8.4.8.8v5.2c.1.5.5.9 1 .9.6 0 1-.5 1-1l-.2-8.6c-.1-1.3-1.2-2.4-2.6-2.4z"
                           ></path>
                        <path d="M2 2h10c1.1 0 2 .9 2 2s-.9 2-2 2H2C.9 6 0 5.1 0 4s.9-2 2-2zm0 6h6c1.1 0 2 .9 2 2s-.9 2-2 2H2c-1.1 0-2-.9-2-2s.9-2 2-2zm0 6h4c1.1 0 2 .9 2 2s-.9 2-2 2H2c-1.1 0-2-.9-2-2s.9-2 2-2zm0 6h2c1.1 0 2 .9 2 2s-.9 2-2 2H2c-1.1 0-2-.9-2-2s.9-2 2-2zm0 6c1.1 0 2 .9 2 2s-.9 2-2 2-2-.9-2-2 .9-2 2-2z"
                              ></path>
                    </svg>
                    <div className='pressure-wrapper'>
                        <div className='pressure-home'
                             style={{width: activeGame.pressure.homePressure + '%'}}>{activeGame.homeTeam.shortName} ({activeGame.pressure.homePressure}%)
                        </div>
                        <div className='pressure-away'
                             style={{width: activeGame.pressure.awayPressure + '%'}}>{activeGame.awayTeam.shortName} ({activeGame.pressure.awayPressure}%)
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default SoccerGameTimeline;
